// Bilingual Terms — renders English when language is `eng`, Spanish
// (Condiciones Generales de Contratación) otherwise. Spain still needs the
// Spanish version for LSSI-CE / RDL 1/2007 compliance, but English readers
// shouldn't be confronted with Spanish legalese when they've picked `eng`.

import { Link } from "react-router-dom";
import { Scroll } from "lucide-react";
import { useLang } from "../lib/i18n";

export default function TermsPage() {
  const lang = useLang();
  const es = lang === "spa";
  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-16 fade-up" data-testid="terms-page">
      <div className="font-mono uppercase tracking-[0.25em] text-xs text-[#525252] mb-4 inline-flex items-center gap-2">
        <Scroll className="w-3.5 h-3.5" strokeWidth={1.5} /> {es ? "Condiciones Generales" : "Terms of Service"}
      </div>
      <h1 className="font-heading text-4xl md:text-5xl text-[#0A0A0A] tracking-tight mb-3">
        {es ? "Condiciones Generales de Contratación" : "Terms of Service"}
      </h1>
      <p className="text-[#4A4030] text-lg">
        {es
          ? "Términos que regulan el uso de Reads y del servicio de suscripción Reads+."
          : "The rules that govern how you use Reads and the Reads+ subscription."}
      </p>

      <div className="mt-12 space-y-8 text-[#0A0A0A] leading-relaxed">
        {es ? <SpanishTerms /> : <EnglishTerms />}
        <p className="text-sm text-[#525252] pt-6 border-t border-[rgba(0,0,0,0.08)]">
          {es ? "Última actualización" : "Last updated"}: {es ? "26 de agosto de 2026" : "26 August 2026"}.
        </p>
      </div>
    </div>
  );
}

function EnglishTerms() {
  return (
    <>
      <Section title="1. Acceptance">
        By creating an account or subscribing to Reads+, you accept these Terms, the <Link to="/legal/aviso-legal" className="underline">Legal Notice</Link> and the <Link to="/legal/privacidad" className="underline">Privacy Policy</Link>. If you disagree, don't use the service.
      </Section>
      <Section title="2. What Reads is">
        <p><strong>Reads (free):</strong> up to 10 books and 50 personalised recommendations per month.</p>
        <p className="mt-2"><strong>Reads+ (paid):</strong> unlimited books and recommendations, custom lists, family sub-accounts, and priority access to new features.</p>
      </Section>
      <Section title="3. Price & payment">
        <ul className="list-disc pl-5 space-y-1 text-[15px]">
          <li><strong>Reads+ Monthly:</strong> €3.99 / month.</li>
          <li><strong>Reads+ Yearly:</strong> €35.99 / year (works out to €3.00 / month — 25% off).</li>
          <li>Prices include applicable taxes where relevant.</li>
          <li>Payment is processed by <strong>Stripe</strong>. Reads never stores your card details.</li>
          <li>Subscription state (renewal date, active entitlements) is managed by <strong>RevenueCat</strong>. RevenueCat only receives a pseudonymous customer ID, the product you bought, and purchase receipts — never your reading activity.</li>
        </ul>
      </Section>
      <Section title="4. Auto-renewal">
        Reads+ subscriptions renew automatically at the end of each period (monthly or yearly) using the saved payment method. You can turn renewal off at any time from <Link to="/account" className="underline">Account &gt; Reads+</Link>. If you turn it off, your Reads+ access continues until the end of the period you already paid for.
      </Section>
      <Section title="5. 14-day withdrawal right (EU consumers)">
        Under EU consumer law (art. 103(m) of Spain's RDL 1/2007) the 14-day right of withdrawal doesn't apply once you start actively using Reads+ (consuming recommendations, creating sub-accounts, creating lists). If you paid but haven't used any Reads+ feature yet, request a full refund within 14 days via the <Link to="/contact" className="underline">Contact page</Link>.
      </Section>
      <Section title="6. Cancellation & refunds">
        <ul className="list-disc pl-5 space-y-1 text-[15px]">
          <li>Cancel Reads+ any time from your account. No penalties.</li>
          <li>Partial refunds for unused months are considered case-by-case — write to us from <Link to="/contact" className="underline">Contact</Link>.</li>
          <li>Reads can suspend or cancel accounts that breach these terms, without refund, for fraud, misuse or repeated violations.</li>
        </ul>
      </Section>
      <Section title="7. Changes to the service">
        We can change Reads+ pricing or features with 30 days' email notice. If the changes don't work for you, you can cancel before they take effect, at no cost.
      </Section>
      <Section title="8. User-generated content">
        Reviews, lists and messages are your responsibility. Reads may remove without notice any content that is offensive, illegal, or infringes third-party rights.
      </Section>
      <Section title="9. Under-14s">
        Users under 8 can&apos;t register. From 8 to 13 inclusive, a parent&apos;s email consent is required (GDPR / Spain&apos;s LOPDGDD).
      </Section>
    </>
  );
}

function SpanishTerms() {
  return (
    <>
      <Section title="1. Aceptación">
        Al crear una cuenta o suscribirse a Reads+, la persona usuaria acepta expresamente las presentes Condiciones Generales, el <Link to="/legal/aviso-legal" className="underline">Aviso Legal</Link> y la <Link to="/legal/privacidad" className="underline">Política de Privacidad</Link>. Si no está de acuerdo, no puede utilizar el servicio.
      </Section>
      <Section title="2. Descripción del servicio">
        <p><strong>Reads (gratuito):</strong> hasta 10 libros y 50 recomendaciones personalizadas al mes.</p>
        <p className="mt-2"><strong>Reads+ (de pago):</strong> libros ilimitados, recomendaciones ilimitadas, listas personalizadas, cuentas familiares (sub-accounts) y acceso prioritario a nuevas funciones.</p>
      </Section>
      <Section title="3. Precio y forma de pago">
        <ul className="list-disc pl-5 space-y-1 text-[15px]">
          <li><strong>Reads+ Mensual:</strong> 3,99 € por mes.</li>
          <li><strong>Reads+ Anual:</strong> 35,99 € por año (equivalente a 3,00 €/mes — un 25 % de descuento).</li>
          <li>Los precios incluyen los impuestos aplicables cuando corresponda.</li>
          <li>El pago se realiza a través de <strong>Stripe</strong>. Reads no almacena datos de tarjeta.</li>
          <li>El estado de la suscripción (fecha de renovación, «entitlements» activos) lo gestiona <strong>RevenueCat</strong>. RevenueCat sólo recibe un ID de cliente seudónimo, el producto que compraste y los recibos de compra — nunca tu actividad de lectura.</li>
        </ul>
      </Section>
      <Section title="4. Renovación automática">
        Las suscripciones a Reads+ se renuevan automáticamente al final de cada periodo (mensual o anual) mediante el método de pago registrado. La persona usuaria puede desactivar la renovación en cualquier momento desde <Link to="/account" className="underline">Cuenta &gt; Reads+</Link>. Si la renovación se desactiva, el acceso Reads+ se mantiene hasta el final del periodo ya pagado.
      </Section>
      <Section title="5. Derecho de desistimiento">
        Conforme al artículo 103.m) del RDL 1/2007, el derecho de desistimiento no aplica una vez la persona usuaria comienza a utilizar activamente las funciones Reads+ (recomendaciones consumidas, sub-accounts creadas, listas creadas). Si no se ha utilizado ninguna función Reads+ tras el pago, puede solicitar reembolso íntegro en los 14 días siguientes contactando desde <Link to="/contact" className="underline">Contacto</Link>.
      </Section>
      <Section title="6. Cancelación y reembolsos">
        <ul className="list-disc pl-5 space-y-1 text-[15px]">
          <li>Puede cancelar Reads+ en cualquier momento desde su cuenta. No se aplican penalizaciones.</li>
          <li>Los reembolsos parciales por meses no consumidos se estudian caso por caso escribiendo desde la página de <Link to="/contact" className="underline">Contacto</Link>.</li>
          <li>Reads se reserva el derecho a suspender o cancelar cuentas que infrinjan estas condiciones, sin reembolso, en casos de fraude, uso indebido o incumplimiento repetido.</li>
        </ul>
      </Section>
      <Section title="7. Cambios en el servicio">
        Reads podrá modificar el precio o las funciones de Reads+ con un preaviso de 30 días por correo electrónico. Si los cambios no le convienen, tiene derecho a cancelar antes de que entren en vigor sin coste adicional.
      </Section>
      <Section title="8. Contenidos publicados por las personas usuarias">
        Las reseñas, listas y mensajes son responsabilidad de quien los publica. Reads podrá retirar sin previo aviso cualquier contenido ofensivo, ilegal o que infrinja derechos de terceros.
      </Section>
      <Section title="9. Menores de edad">
        Las personas menores de 8 años no pueden registrarse. Entre 8 y 13 años inclusive es necesaria autorización expresa por email de un progenitor o tutor legal.
      </Section>
    </>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-heading text-2xl md:text-3xl text-[#0A0A0A] tracking-tight mb-3">{title}</h2>
      <div className="text-[15px] text-[#0A0A0A]">{children}</div>
    </section>
  );
}
